import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useGroupInvoices } from '@/hooks/useGroupInvoices';
import type { AccountSummary } from '@/types/domain';

interface AccountSummaryRow {
  customer_id: string;
  name: string;
  payment_terms: string;
  on_credit_hold: boolean;
  credit_limit: number | string | null;
  open_balance: number | string;
  open_invoice_count: number;
  oldest_days_past_due: number;
  past_due_balance: number | string;
}

/**
 * The raw per-account figures straight from `orders.v_account_summary`, RLS
 * scoped to the caller's own customer. This is what anything DECIDING
 * something about this account (credit hold, whether to block an order) must
 * use — see useAccountSummary below for why the displayed figure differs.
 */
export function useAccountOnlySummary() {
  return useQuery<AccountSummary | null>({
    queryKey: ['account-summary'],
    queryFn: async (): Promise<AccountSummary | null> => {
      const { data, error } = await supabase
        .schema('orders')
        .from('v_account_summary')
        .select(
          'customer_id, name, payment_terms, on_credit_hold, credit_limit, open_balance, open_invoice_count, oldest_days_past_due, past_due_balance',
        )
        .maybeSingle();

      if (error) {
        // View not yet pushed or empty — quietly return null.
        return null;
      }
      if (!data) return null;

      const row = data as AccountSummaryRow;
      return {
        customerId: row.customer_id,
        name: row.name,
        paymentTerms: row.payment_terms,
        onCreditHold: row.on_credit_hold,
        creditLimit: row.credit_limit !== null ? Number(row.credit_limit) : null,
        openBalance: Number(row.open_balance ?? 0),
        openInvoiceCount: row.open_invoice_count,
        oldestDaysPastDue: row.oldest_days_past_due,
        pastDueBalance: Number(row.past_due_balance ?? 0),
      };
    },
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * useAccountSummary — the balance to SHOW for the account you're standing on.
 *
 * On an ordinary customer that is simply their own balance. On a chain's
 * master, standing there means the whole chain: the invoice list, the merged
 * statement and the pay run all already mean every store, so a Home page
 * quoting only the master's own handful of invoices contradicts every other
 * surface in the app. It reads as "where did everything go" — which is exactly
 * what happened before this existed.
 *
 * ⚠ It swaps the MONEY, not the identity: name, terms, credit hold and credit
 * limit stay the master's own, because those are facts about this account and
 * not about the group.
 *
 * ⚠ Anything that GATES on the number (the past-due block on ordering) must
 * call `useAccountOnlySummary` instead. Blocking a corporate login from
 * ordering because 26 stores collectively owe money is a different policy than
 * the one that exists today, and not one this change is entitled to make.
 */
export function useAccountSummary() {
  const account = useAccountOnlySummary();
  const { data: group } = useGroupInvoices();

  const data = useMemo(() => {
    if (!account.data) return account.data;
    if (!group?.is_group || !group.totals) return account.data;
    return {
      ...account.data,
      openBalance: group.totals.open_balance,
      openInvoiceCount: group.totals.open_count,
      pastDueBalance: group.totals.past_due_balance,
    };
  }, [account.data, group]);

  return { ...account, data };
}
